import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./revenue.css";
import {
  getRevenueCategories,
  getRevenueGifts,
  getRevenueSummary,
  getRevenueUsers,
  errorMessage,
} from "./revenueApi";
import { Kpi, Loading, Pager, Progress, RangeBar, RevenueChart, SplitBar, UserCell, Avatar } from "./parts";
import { CYCLE_STATUS, RUN_STATUS, compact, dateTime, duration, fmt } from "./format";

/// Home → gift revenue: totals for any date range, who earned what, every
/// category's share and goal cycle, and the raw split of each gift.
export default function RevenueSection() {
  const [range, setRange] = useState({ from: "", to: "", preset: "all" });
  const [bucket, setBucket] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("users");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getRevenueSummary({ from: range.from, to: range.to, bucket }));
    } catch (e) {
      setError(errorMessage(e, "Could not load revenue"));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, bucket]);

  useEffect(() => {
    load();
  }, [load]);

  const s = data?.summary;
  const p = data?.percentages;
  const appFromGifts = s ? s.app.total - s.app.pending_forfeit : 0;

  return (
    <div className="rv-wrap mt-4" data-aos="fade-up" data-aos-duration="800">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
        <div>
          <h2 className="rv-title">Gift revenue</h2>
          <p className="rv-sub">
            {p
              ? `Store ${p.store}% first · then hosts ${p.host}% · no target: app ${100 - p.host}% · with a category target: category ${p.category}%, app ${
                  100 - p.host - p.category - p.pending
                }%, pending ${p.pending}%`
              : "Where every gifted coin went"}
          </p>
        </div>
        <RangeBar value={range} onChange={setRange} />
      </div>

      {error && <div className="rv-error mb-3">{error}</div>}
      {loading && !data ? (
        <Loading />
      ) : s ? (
        <>
          <div className="rv-kpis mb-3">
            <Kpi
              label="Gifts value"
              value={compact(s.gross)}
              title={fmt(s.gross)}
              foot={
                <>
                  <b>{fmt(s.gifts_count)}</b> gifts · <b>{fmt(s.counts.senders)}</b> senders
                </>
              }
            />
            <Kpi
              tone="gold"
              label={`Store + margin (${p?.store ?? 30}%)`}
              value={compact(s.store)}
              title={fmt(s.store)}
              foot={
                <>
                  Net after store: <b>{fmt(s.net)}</b>
                </>
              }
            />
            <Kpi
              tone="pink"
              label="Hosts · diamonds"
              value={compact(s.host.total)}
              title={fmt(s.host.total)}
              foot={
                <>
                  With target <b>{fmt(s.host.with_target)}</b> ({fmt(s.counts.receivers_with_target)} users)
                  <br />
                  Without <b>{fmt(s.host.without_target)}</b> ({fmt(s.counts.receivers_without_target)} users)
                </>
              }
            />
            <Kpi
              tone="violet"
              label="App revenue"
              value={compact(s.app.total)}
              title={fmt(s.app.total)}
              foot={
                <>
                  No target <b>{fmt(s.app.without_target)}</b> · target <b>{fmt(s.app.with_target)}</b>
                  <br />
                  Expired pending <b>{fmt(s.app.pending_forfeit)}</b>
                </>
              }
            />
            <Kpi
              tone="green"
              label="Categories"
              value={compact(s.category.total)}
              title={fmt(s.category.total)}
              foot={
                <>
                  Direct <b>{fmt(s.category.direct)}</b> · released <b>{fmt(s.category.released)}</b>
                  <br />
                  {fmt(s.counts.categories)} categories earned
                </>
              }
            />
            <Kpi
              tone="grey"
              label="Pending held now"
              value={compact(s.pending.held_now)}
              title={fmt(s.pending.held_now)}
              foot={
                <>
                  Created <b>{fmt(s.pending.created)}</b> · released <b>{fmt(s.pending.released)}</b>
                  <br />
                  Went to app <b>{fmt(s.pending.forfeited)}</b>
                </>
              }
            />
          </div>

          <div className="rv-panel mb-3">
            <div className="rv-panel-title">Where the coins went</div>
            <SplitBar
              parts={[
                { label: "Store", value: s.store, color: "#e4ba56" },
                { label: "Hosts", value: s.host.total, color: "#fc155c" },
                { label: "App", value: appFromGifts, color: "#7437d0" },
                { label: "Categories (direct)", value: s.category.direct, color: "#01b574" },
                { label: "Pending pool", value: s.pending.created, color: "#a96fff" },
              ]}
            />
          </div>

          <div className="rv-panel mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <div className="rv-panel-title mb-0">
                Trend <span className="text-grey fs-6">({data.timeseries.bucket === "day" ? "per day" : "per month"})</span>
              </div>
              <div className="rv-seg violet">
                {[
                  ["", "Auto"],
                  ["day", "Day"],
                  ["month", "Month"],
                ].map(([k, l]) => (
                  <button key={k || "auto"} className={bucket === k ? "active" : ""} onClick={() => setBucket(k)}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <RevenueChart points={data.timeseries.points} />
          </div>

          {data.adjustments?.length > 0 && (
            <div className="rv-panel mb-3">
              <div className="rv-panel-title">Manual balance changes in range</div>
              <div className="d-flex flex-wrap gap-4">
                {data.adjustments.map((a, i) => (
                  <div key={i}>
                    <div className="text-grey small text-capitalize">
                      {a.owner_type} · {a.currency} · {a.reason === "admin_credit" ? "added" : "subtracted"}
                    </div>
                    <div className={a.amount >= 0 ? "rv-amount-pos" : "rv-amount-neg"}>
                      {a.amount >= 0 ? "+" : ""}
                      {fmt(a.amount)} <span className="text-grey small">({a.count})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : null}

      <div className="rv-panel">
        <div className="rv-tabs mb-3">
          {[
            ["users", "Top users"],
            ["categories", "Categories"],
            ["gifts", "Gift log"],
          ].map(([k, l]) => (
            <button key={k} className={`rv-tab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)}>
              {l}
            </button>
          ))}
        </div>
        {tab === "users" && <UsersTab range={range} />}
        {tab === "categories" && <CategoriesTab range={range} />}
        {tab === "gifts" && <GiftsTab range={range} />}
      </div>
    </div>
  );
}

function UsersTab({ range }) {
  const navigate = useNavigate();
  const [hasTarget, setHasTarget] = useState("");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("diamonds");
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => setPage(1), [range.from, range.to, hasTarget, query, sort]);

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    getRevenueUsers({ from: range.from, to: range.to, has_target: hasTarget, q: query, sort, page, limit: 20 })
      .then((r) => alive && setRes(r))
      .catch((e) => alive && setError(errorMessage(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [range.from, range.to, hasTarget, query, sort, page]);

  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div className="rv-seg">
          {[
            ["", "All"],
            ["1", "With target"],
            ["0", "Without target"],
          ].map(([k, l]) => (
            <button key={k || "all"} className={hasTarget === k ? "active" : ""} onClick={() => setHasTarget(k)}>
              {l}
            </button>
          ))}
        </div>
        <input className="rv-input" style={{ maxWidth: 240 }} placeholder="Search name…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="rv-select" style={{ maxWidth: 220 }} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="diamonds">Sort: diamonds</option>
          <option value="gross">Sort: gifts value</option>
          <option value="gifts_count">Sort: gifts count</option>
          <option value="category_generated">Sort: generated for category</option>
        </select>
      </div>
      {error && <div className="rv-error mb-2">{error}</div>}
      {loading && !res ? (
        <Loading />
      ) : !res?.data?.length ? (
        <div className="rv-empty">No users in this range.</div>
      ) : (
        <>
          <div className="rv-table-wrap">
            <table className="rv-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th className="rv-num">Gifts</th>
                  <th className="rv-num">Senders</th>
                  <th className="rv-num">Gifts value</th>
                  <th className="rv-num">Diamonds</th>
                  <th className="rv-num">With target</th>
                  <th className="rv-num">Without</th>
                  <th className="rv-num">For category</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {res.data.map((r) => (
                  <tr key={r.user.id} className="rv-click" onClick={() => navigate(`/dashboard/revenue/users/${r.user.id}`)}>
                    <td className="text-grey">{r.rank}</td>
                    <td>
                      <UserCell user={r.user} />
                    </td>
                    <td className="rv-num">{fmt(r.gifts_count)}</td>
                    <td className="rv-num">{fmt(r.senders)}</td>
                    <td className="rv-num">{fmt(r.gross)}</td>
                    <td className="rv-num fw-bold">{fmt(r.diamonds)}</td>
                    <td className="rv-num">{fmt(r.diamonds_with_target)}</td>
                    <td className="rv-num">{fmt(r.diamonds_without_target)}</td>
                    <td className="rv-num">{fmt(r.category_generated)}</td>
                    <td>
                      {r.target?.category ? (
                        <span className="rv-badge rv-badge-violet">{r.target.category.name}</span>
                      ) : (
                        <span className="rv-badge rv-badge-grey">No target</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager meta={res.meta} onPage={setPage} />
        </>
      )}
    </>
  );
}

function CategoriesTab({ range }) {
  const navigate = useNavigate();
  const [sort, setSort] = useState("earned");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    getRevenueCategories({ from: range.from, to: range.to, sort })
      .then((r) => alive && setRows(r.data))
      .catch((e) => alive && setError(errorMessage(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [range.from, range.to, sort]);

  return (
    <>
      <div className="d-flex gap-2 mb-3">
        <select className="rv-select" style={{ maxWidth: 240 }} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="earned">Sort: earned</option>
          <option value="net_generated">Sort: coins generated</option>
          <option value="gifts_count">Sort: gifts</option>
          <option value="users">Sort: users</option>
          <option value="pending_created">Sort: pending</option>
        </select>
      </div>
      {error && <div className="rv-error mb-2">{error}</div>}
      {loading && !rows ? (
        <Loading />
      ) : !rows?.length ? (
        <div className="rv-empty">No categories yet.</div>
      ) : (
        <div className="rv-table-wrap">
          <table className="rv-table">
            <thead>
              <tr>
                <th>Category</th>
                <th className="rv-num">Balance</th>
                <th>Status</th>
                <th className="rv-num">Members</th>
                <th className="rv-num">Users</th>
                <th className="rv-num">Coins generated</th>
                <th className="rv-num">Earned</th>
                <th className="rv-num">Pending held</th>
                <th style={{ minWidth: 220 }}>Current cycle</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const runStatus = r.category.run_status || "stopped";
                // A stopped category has no current cycle (the list may still
                // report the last closed one) — only show it while running/paused.
                const c = runStatus === "stopped" ? null : r.current_cycle;
                const st = c ? CYCLE_STATUS[c.status] : null;
                const rs = RUN_STATUS[runStatus];
                return (
                  <tr key={r.category.id} className="rv-click" onClick={() => navigate(`/dashboard/revenue/categories/${r.category.id}`)}>
                    <td>
                      <div className="rv-user">
                        <Avatar img={r.category.img} name={r.category.name} />
                        <div className="rv-user-name">{r.category.name}</div>
                      </div>
                    </td>
                    <td className="rv-num">{fmt(r.category.coins)}</td>
                    <td>
                      <span className={`rv-badge ${r.category.is_default ? "rv-badge-grey" : rs?.cls}`}>
                        {r.category.is_default ? "Default" : rs?.label}
                      </span>
                    </td>
                    <td className="rv-num">{fmt(r.members ?? r.active_targets)}</td>
                    <td className="rv-num">{fmt(r.users)}</td>
                    <td className="rv-num">{fmt(r.net_generated)}</td>
                    <td className="rv-num fw-bold">{fmt(r.earned)}</td>
                    <td className="rv-num">{fmt(c?.pending_coins || 0)}</td>
                    <td>
                      {c ? (
                        <>
                          <div className="d-flex justify-content-between small mb-1">
                            <span>
                              {compact(c.achieved_coins)} / {compact(c.goal_coins)}
                            </span>
                            <span className={`rv-badge ${st?.cls}`}>{st?.label}</span>
                          </div>
                          <Progress value={c.progress} size="sm" />
                          <div className="text-grey small mt-1">
                            {c.paused ? `Paused · ${duration(c.seconds_left)} left` : `${duration(c.seconds_left)} left`}
                          </div>
                        </>
                      ) : (
                        <span className="text-grey small">
                          {r.category.is_default ? "No revenue share" : "Stopped — nothing is counted"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function GiftsTab({ range, categoryId, userId }) {
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => setPage(1), [range.from, range.to, categoryId, userId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    getRevenueGifts({ from: range.from, to: range.to, category_id: categoryId, user_id: userId, page, limit: 20 })
      .then((r) => alive && setRes(r))
      .catch((e) => alive && setError(errorMessage(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [range.from, range.to, categoryId, userId, page]);

  if (error) return <div className="rv-error">{error}</div>;
  if (loading && !res) return <Loading />;
  if (!res?.data?.length) return <div className="rv-empty">No gifts in this range.</div>;

  return (
    <>
      <div className="rv-table-wrap">
        <table className="rv-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>From</th>
              <th>To</th>
              <th>Gift</th>
              <th className="rv-num">Value</th>
              <th className="rv-num">Store</th>
              <th className="rv-num">Host</th>
              <th className="rv-num">Category</th>
              <th className="rv-num">Pending</th>
              <th className="rv-num">App</th>
            </tr>
          </thead>
          <tbody>
            {res.data.map((g) => (
              <tr key={g.id}>
                <td className="text-grey small" style={{ whiteSpace: "nowrap" }}>
                  {dateTime(g.created_at)}
                </td>
                <td>
                  <UserCell user={g.sender} />
                </td>
                <td>
                  <UserCell user={g.receiver} sub={g.category ? g.category.name : "No target"} />
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {g.gift?.name || "Gift"} × {g.count}
                </td>
                <td className="rv-num fw-bold">{fmt(g.gross)}</td>
                <td className="rv-num">{fmt(g.store)}</td>
                <td className="rv-num">{fmt(g.host)}</td>
                <td className="rv-num">{fmt(g.category_share)}</td>
                <td className="rv-num">{fmt(g.pending)}</td>
                <td className="rv-num">{fmt(g.app)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager meta={res.meta} onPage={setPage} />
    </>
  );
}
